// src/components/Minihome/MinihomeEditor.jsx

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Stack, Button, TextField, Switch, FormControlLabel, Alert, Typography, Divider, Checkbox, // <- 여기서 IconButton 삭제
    Accordion, AccordionSummary, AccordionDetails, ToggleButtonGroup, ToggleButton,
    Select, MenuItem, InputLabel, FormControl, ListItemText, OutlinedInput, Slider, Tooltip
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import UpdateIcon from '@mui/icons-material/Update'; // ✅ '일일 집계'용 아이콘
import FunctionsIcon from '@mui/icons-material/Functions'; // ✅ '누적 집계'용 아이콘
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import RGL, { WidthProvider } from "react-grid-layout";
import { useUser } from '../../contexts/UserContext';
import WidgetRegistry from "./WidgetRegistry";
// ✅ getMyUnlocks, buyUnlock 추가
import { getMyMinihome, patchMyMinihome, getMyUnlocks, buyUnlock } from "../../api/minihome";
import { getMyTitles } from "../../api/titles";
import { getMyEmoticons } from "../../api/emoticons";
import LockIcon from '@mui/icons-material/Lock';

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const GridLayout = WidthProvider(RGL);
const DEFAULT_GRID = { cols: 12, rowHeight: 20, margin: [8, 8] };
const DEFAULT_THEME = { background: { type: 'color', value: '#ffffff', emote: null, size: 48, opacity: 0.1, repeatMode: 'pattern' } };
const TRANSPARENT_COLOR = 'transparent';

// ✅ 해금 관련 상수 정의
const UNLOCK_KEYS = {
    reactionBar: 'widget.reactionBar',
    emoteBackground: 'theme.background.emote',
    titleShowcase: 'widget.titleShowcase',
    emoteShelf: 'widget.emoteShelf',
    textBox: 'widget.textBox',
};

export default function MinihomeEditor() {
    const { refreshUserInfo } = useUser();
    const navigate = useNavigate();
    const [layout, setLayout] = useState([]);
    const [title, setTitle] = useState("");
    const [isPublic, setIsPublic] = useState(true);
    const [theme, setTheme] = useState(DEFAULT_THEME);
    const [username, setUsername] = useState("");
    const [saving, setSaving] = useState(false);
    const [saveOk, setSaveOk] = useState("");
    const [saveErr, setSaveErr] = useState("");
    const [expanded, setExpanded] = useState(false);
    const [ownedTitles, setOwnedTitles] = useState([]);
    const [ownedEmotes, setOwnedEmotes] = useState([]);
    const [ownedUnlocks, setOwnedUnlocks] = useState([]);
    const [unlockCosts, setUnlockCosts] = useState({});
    const [unlocking, setUnlocking] = useState(null); // 현재 해금 중인 아이템의 key 저장
    const [loading, setLoading] = useState(true); // ✅ 페이지 초기 로딩 상태 추가


    useEffect(() => {
        (async () => {
            try {
                const { data: homeData } = await getMyMinihome();
                const initialItems = homeData?.layout?.items ?? [];
                const validTypes = Object.keys(WidgetRegistry);
                const sanitizedItems = initialItems
                    .filter(item => validTypes.includes(item.type))
                    .map((item, index) => ({
                        ...item,
                        i: String(item.i || `w-${Date.now()}-${index}`),
                        type: item.type,
                        x: item.x || 0, y: item.y || 0, w: item.w || 4, h: item.h || 6,
                        backgroundColor: item.backgroundColor || 'rgba(255,255,255,0.8)',
                        introText: item.introText || '',
                        isTransparent: item.isTransparent || false,
                        showcaseConfig: item.showcaseConfig || null,
                        emoteConfig: item.emoteConfig || { header: "이모티콘", layoutMode: "grid", emotes: [], size: 48, gap: 8 },
                        textConfig: item.textConfig || { content: '', textAlign: 'left' }, // textAlign 기본값 추가
                    }));
                setLayout(sanitizedItems);
                setTitle(homeData?.title ?? "미니홈");
                setIsPublic(Boolean(homeData?.is_public));
                const serverTheme = homeData?.theme || {};
                const newTheme = {
                    ...DEFAULT_THEME, ...serverTheme,
                    background: { ...DEFAULT_THEME.background, ...(serverTheme.background || {}) },
                };
                setTheme(newTheme);
                setUsername(homeData?.username ?? "");

                // ✅ 해금 정보 로딩 로직 추가
                const { data: unlockData } = await getMyUnlocks();
                setOwnedUnlocks(unlockData.unlocks || []);
                setUnlockCosts(unlockData.costs || {});

                const myOwnedTitles = await getMyTitles();
                setOwnedTitles(myOwnedTitles);
                // 모든 로딩이 끝난 후
                const myEmoteList = await getMyEmoticons();
                setOwnedEmotes(myEmoteList || []);
            } catch (e) {
                console.error("LOAD ERROR:", e);
                setSaveErr("미니홈 정보를 불러오지 못했습니다.");
            } finally {
                setLoading(false); // ✅ 성공하든 실패하든 로딩 상태를 false로 변경
            }
        })();
    }, []);

    const handleThemeChange = (prop, value) => {
        setTheme(prev => ({
            ...prev,
            background: { ...prev.background, [prop]: value }
        }));
    };

    const backgroundStyle = useMemo(() => {
        const bg = theme.background || {};
        const baseStyle = {
            width: '100%',
            height: '100%',
            minHeight: '100vh',
            position: 'relative',
            backgroundColor: bg.type === 'color' ? bg.value : '#ffffff',
        };

        if (bg.type === 'emote' && bg.emote) {
            const isUrl = bg.emote.startsWith('/') || bg.emote.startsWith('http');
            const size = bg.size || 48;
            const opacity = bg.opacity || 0.1;
            const repeatMode = bg.repeatMode || 'pattern';

            let imageUrl = bg.emote;

            if (!isUrl) {
                const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size * 1.5}" height="${size * 1.5}"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="${size}px">${bg.emote}</text></svg>`;
                imageUrl = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
            } else {
                imageUrl = `url(${imageUrl})`;
            }

            // ✅ 'cover' 모드 추가
            if (repeatMode === 'cover') {
                return {
                    ...baseStyle,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundImage: imageUrl,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center center',
                        backgroundSize: 'cover', // ✅ 화면을 꽉 채우는 핵심 속성
                        opacity: opacity,
                        zIndex: 0,
                    }
                };
            }

            if (repeatMode === 'single') {
                return {
                    ...baseStyle,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundImage: imageUrl,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center center',
                        backgroundSize: 'contain',
                        opacity: opacity,
                        zIndex: 0,
                    }
                };
            }

            // 'pattern' 모드 (기존 로직)
            return {
                ...baseStyle,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundImage: imageUrl,
                    backgroundRepeat: 'repeat',
                    backgroundSize: `${size * 1.5}px ${size * 1.5}px`,
                    opacity: opacity,
                    zIndex: 0,
                }
            };
        }
        return baseStyle;
    }, [theme]);

    const rglLayout = useMemo(() => layout.map(item => ({ i: item.i, x: item.x, y: item.y, w: item.w, h: item.h })), [layout]);

    const onLayoutChange = (newLayoutFromRGL) => {
        const currentItemsMap = new Map(layout.map(item => [item.i, item]));
        const mergedLayout = newLayoutFromRGL.map(newItem => ({ ...currentItemsMap.get(newItem.i), ...newItem }));
        setLayout(mergedLayout);
    };

    const hasAbout = useMemo(() => layout.some(item => item.type === 'about'), [layout]);
    const hasGuestbook = useMemo(() => layout.some(item => item.type === 'guestbook'), [layout]);

    const onAddWidget = (type) => {
        const isSingleInstance = ['about', 'guestbook'].includes(type);
        if (isSingleInstance && layout.some(item => item.type === type)) { return; }
        const newItem = { i: `${type}-${Date.now()}`, type, x: 0, y: Infinity, isTransparent: false };
        if (type === 'about') {
            Object.assign(newItem, { w: 4, h: 8, backgroundColor: 'rgba(255,255,255,0.8)', introText: '' });
        } else if (type === 'guestbook') {
            Object.assign(newItem, { w: 8, h: 12, backgroundColor: 'rgba(255,255,255,0.8)' });
        } else if (type === 'titleShowcase') {
            Object.assign(newItem, {
                w: 8, h: 6,
                backgroundColor: 'rgba(255,255,255,0.8)',
                showcaseConfig: {
                    header: "나의 칭호 목록",
                    layoutMode: "grid",
                    selectedIds: [],
                    badgeBackgroundColor: '#e0e0e0',
                    badgeTextColor: '#000000',
                }
            });
        } else if (type === 'emoteShelf') {
            Object.assign(newItem, { w: 8, h: 6, backgroundColor: 'rgba(255,255,255,0.8)', emoteConfig: { header: "이모티콘", layoutMode: "grid", emotes: [], size: 48, gap: 8 } });
        }
        // ✅ 아래 else if 블록 전체 추가
        else if (type === 'reactionBar') {
            Object.assign(newItem, {
                w: 6,
                h: 3,
                backgroundColor: 'rgba(255,255,255,0.8)',
                // ✅ 아래 reactionConfig 객체 추가
                reactionConfig: {
                    displayMode: 'daily', // 'daily' | 'cumulative'
                    enabledTypes: ['heart', 'thumb', 'leaf'],
                }
            });
        }

        // 👇 'textBox' 타입 추가 로직 시작
        else if (type === 'textBox') {
            Object.assign(newItem, {
                w: 4, h: 6, // 기본 크기
                backgroundColor: 'rgba(255,255,255,0.8)',
                textConfig: {
                    content: '텍스트 박스\n\n- Markdown을 지원합니다.\n- *기울임*이나 **굵게**도 가능해요.',
                    textAlign: 'left' // textAlign 기본값 추가
                }
            });
        }
        setLayout(prev => [...prev, newItem]);
    };

    const onRemoveWidget = (itemId) => {
        setLayout(prev => prev.filter(item => item.i !== itemId));
    };

    const onSave = async () => {
        setSaving(true); setSaveOk(""); setSaveErr("");
        try {
            const payload = { title, is_public: isPublic, theme, layout: { grid: DEFAULT_GRID, items: layout } };
            const { data } = await patchMyMinihome(payload);
            setSaveOk("저장되었습니다.");
            if (data?.username) { navigate(`/u/${encodeURIComponent(data.username)}/home?ts=${Date.now()}`); }
        } catch (e) {
            console.error("SAVE ERROR:", e);
            const msg = e?.response?.data?.detail || e?.message || "저장에 실패했습니다."; setSaveErr(msg);
        } finally {
            setSaving(false); setTimeout(() => setSaveOk(""), 2000);
        }
    };

    const handleWidgetPropChange = (itemId, propName, value) => {
        setLayout(prev => prev.map(item => {
            if (item.i !== itemId) return item;
            const updated = { ...item, [propName]: value };
            if (propName === 'isTransparent') {
                updated.backgroundColor = value ? TRANSPARENT_COLOR : (item.backgroundColor === TRANSPARENT_COLOR ? 'rgba(255,255,255,0.8)' : item.backgroundColor);
            }
            return updated;
        }));
    };

    const getWidgetProp = (itemId, propName, defaultValue) => {
        return layout.find(item => item.i === itemId)?.[propName] ?? defaultValue;
    };

    const homeForWidget = useMemo(() => ({ title, username }), [title, username]);
    const handleAccordionChange = (panel) => (_, isExpanded) => setExpanded(isExpanded ? panel : false);

    // ✅ 해금 버튼 클릭 시 실행될 핸들러 함수 추가
    const handleUnlock = async (key) => {
        if (unlocking) return; // 중복 클릭 방지

        const cost = unlockCosts[key] || 0;
        if (!window.confirm(`${cost}Leaf🌿를 사용하여 이 기능을 해금하시겠습니까?`)) {
            return;
        }

        setUnlocking(key);
        setSaveErr(""); // 이전 에러 메시지 초기화
        try {
            const { data } = await buyUnlock(key);
            setOwnedUnlocks(data.unlocks); // 성공 시 최신 해금 목록으로 state 업데이트
            await refreshUserInfo();
        } catch (e) {
            const msg = e?.response?.data?.detail || "해금에 실패했습니다. Leaf가 부족하지 않은지 확인해주세요.";
            setSaveErr(msg);
        } finally {
            setUnlocking(null); // 로딩 상태 해제
        }
    };
    return (
        <Box sx={{ display: "grid", gridTemplateColumns: { md: "320px 1fr" }, minHeight: "100vh" }}>
            <Stack spacing={2} sx={{ p: 2, borderRight: { md: "1px solid #eee" }, overflowY: 'auto' }}>
                <TextField label="홈 제목" size="small" value={title} onChange={(e) => setTitle(e.target.value)} />
                <FormControlLabel control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />} label={isPublic ? "공개" : "비공개"} />

                <Stack spacing={1.5} sx={{ border: '1px solid #eee', p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2">전체 배경</Typography>
                    <ToggleButtonGroup
                        value={theme.background?.type || 'color'}
                        exclusive
                        size="small"
                        onChange={(_, newType) => newType && handleThemeChange('type', newType)}
                        fullWidth
                    >
                        {/* ✅ sx={{ flex: 1 }} 추가 */}
                        <ToggleButton value="color" sx={{ flex: 1 }}>
                            색상
                        </ToggleButton>

                        <Tooltip title={!ownedUnlocks.includes(UNLOCK_KEYS.emoteBackground) ? "이모티콘 배경은 해금이 필요합니다." : ""}>
                            {/* ✅ span에도 flex 스타일을 적용해 공간을 차지하게 합니다. */}
                            <span style={{ display: 'flex', flex: 1 }}>
                                <ToggleButton
                                    value="emote"
                                    disabled={!ownedUnlocks.includes(UNLOCK_KEYS.emoteBackground)}
                                    // ✅ 버튼 자체도 너비를 100%로 채웁니다.
                                    sx={{ flex: 1, width: '100%' }}
                                >
                                    이모티콘
                                </ToggleButton>
                            </span>
                        </Tooltip>
                    </ToggleButtonGroup>

                    {theme.background?.type === 'color' && (
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 1 }}>
                            <input type="color" value={theme.background.value || '#ffffff'} onChange={e => handleThemeChange('value', e.target.value)} style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer' }} />
                            <TextField label="HEX" variant="outlined" size="small" value={theme.background.value || '#ffffff'} onChange={e => handleThemeChange('value', e.target.value)} fullWidth />
                        </Stack>
                    )}

                    {theme.background?.type === 'emote' && (
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            <ToggleButtonGroup
                                value={theme.background.repeatMode || 'pattern'}
                                exclusive size="small"
                                onChange={(_, newMode) => newMode && handleThemeChange('repeatMode', newMode)}
                                fullWidth
                            >
                                <ToggleButton value="pattern">패턴</ToggleButton>
                                <ToggleButton value="single">크게 하나</ToggleButton>
                                <ToggleButton value="cover">채우기</ToggleButton>
                            </ToggleButtonGroup>
                            <FormControl size="small" fullWidth>
                                <InputLabel>이모티콘 선택</InputLabel>
                                <Select
                                    value={theme.background.emote || ''}
                                    label="이모티콘 선택"
                                    onChange={(e) => handleThemeChange('emote', e.target.value)}
                                >
                                    {(ownedEmotes || []).map((em, idx) => (
                                        <MenuItem key={`${em.value}-${idx}`} value={em.value}>
                                            <Stack direction="row" spacing={1.2} alignItems="center">
                                                {em.image ? <Box component="img" src={em.value} sx={{ width: 24, height: 24, objectFit: 'contain' }} /> : <Box>{em.value}</Box>}
                                                <ListItemText primary={em.name} />
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box>
                                <Typography variant="caption">
                                    {theme.background.repeatMode === 'pattern' ? '패턴 크기' : '크기 (자동 조절)'}
                                </Typography>

                                {/* ✅ 패턴 모드에서만 크기 슬라이더 표시 */}
                                {theme.background.repeatMode === 'pattern' && (
                                    <Slider
                                        value={theme.background.size || 48}
                                        onChange={(_, v) => handleThemeChange('size', v)}
                                        min={16} max={128} step={4}
                                        valueLabelDisplay="auto"
                                    />
                                )}
                            </Box>
                            <Box>
                                <Typography variant="caption">투명도</Typography>
                                <Slider value={theme.background.opacity || 0.1} onChange={(_, v) => handleThemeChange('opacity', v)} min={0.05} max={1} step={0.05} valueLabelDisplay="auto" />
                            </Box>
                        </Stack>
                    )}
                </Stack>

                <Divider />
                <Typography variant="subtitle2" sx={{ mt: 1 }}>기능 해금 및 위젯 추가</Typography>
                <Stack direction="column" spacing={1}>
                    {/* --- 이모티콘 배경 해금 버튼 --- */}
                    {!ownedUnlocks.includes(UNLOCK_KEYS.emoteBackground) && (
                        <Button variant="contained" color="secondary" startIcon={<LockIcon />} onClick={() => handleUnlock(UNLOCK_KEYS.emoteBackground)} disabled={unlocking === UNLOCK_KEYS.emoteBackground} >
                            {unlocking === UNLOCK_KEYS.emoteBackground ? "해금 중..." : `${unlockCosts[UNLOCK_KEYS.emoteBackground] || '?'}Leaf🌿로 이모티콘 배경 해금`}
                        </Button>
                    )}
                    {/* --- 응원하기 위젯 해금/추가 버튼 --- */}
                    {ownedUnlocks.includes(UNLOCK_KEYS.reactionBar) ? (
                        <Button variant="outlined" onClick={() => onAddWidget("reactionBar")}>응원위젯 추가</Button>
                    ) : (
                        <Button variant="contained" color="secondary" startIcon={<LockIcon />} onClick={() => handleUnlock(UNLOCK_KEYS.reactionBar)} disabled={unlocking === UNLOCK_KEYS.reactionBar} >
                            {unlocking === UNLOCK_KEYS.reactionBar ? "해금 중..." : `${unlockCosts[UNLOCK_KEYS.reactionBar] || '?'}Leaf🌿로 응원 위젯 해금`}
                        </Button>
                    )}

                    {/* --- 칭호 진열장 위젯 해금/추가 버튼 --- */}
                    {ownedUnlocks.includes(UNLOCK_KEYS.titleShowcase) ? (
                        <Button variant="outlined" onClick={() => onAddWidget("titleShowcase")}>칭호 진열장 추가</Button>
                    ) : (
                        <Button variant="contained" color="secondary" startIcon={<LockIcon />} onClick={() => handleUnlock(UNLOCK_KEYS.titleShowcase)} disabled={unlocking === UNLOCK_KEYS.titleShowcase} >
                            {unlocking === UNLOCK_KEYS.titleShowcase ? "해금 중..." : `${unlockCosts[UNLOCK_KEYS.titleShowcase] || '?'}Leaf🌿로 칭호 진열장 해금`}
                        </Button>
                    )}

                    {/* --- 이모티콘 진열장 위젯 해금/추가 버튼 --- */}
                    {ownedUnlocks.includes(UNLOCK_KEYS.emoteShelf) ? (
                        <Button variant="outlined" onClick={() => onAddWidget("emoteShelf")}>이모티콘 진열장 추가</Button>
                    ) : (
                        <Button variant="contained" color="secondary" startIcon={<LockIcon />} onClick={() => handleUnlock(UNLOCK_KEYS.emoteShelf)} disabled={unlocking === UNLOCK_KEYS.emoteShelf} >
                            {unlocking === UNLOCK_KEYS.emoteShelf ? "해금 중..." : `${unlockCosts[UNLOCK_KEYS.emoteShelf] || '?'}Leaf🌿로 이모티콘 진열장 해금`}
                        </Button>
                    )}
                    {/* --- 텍스트 박스 위젯 해금/추가 버튼 --- */}
                    {ownedUnlocks.includes(UNLOCK_KEYS.textBox) ? (
                        <Button variant="outlined" onClick={() => onAddWidget("textBox")}>텍스트 박스 추가</Button>
                    ) : (
                        <Button variant="contained" color="secondary" startIcon={<LockIcon />} onClick={() => handleUnlock(UNLOCK_KEYS.textBox)} disabled={unlocking === UNLOCK_KEYS.textBox} >
                            {unlocking === UNLOCK_KEYS.textBox ? "해금 중..." : `${unlockCosts[UNLOCK_KEYS.textBox] || '200'}Leaf🌿로 텍스트 박스 해금`}
                        </Button>
                    )}

                    {/* --- 기본 제공 위젯 추가 버튼들 --- */}
                    <Button variant="outlined" disabled={hasAbout} onClick={() => onAddWidget("about")}>소개카드 추가</Button>
                    <Button variant="outlined" disabled={hasGuestbook} onClick={() => onAddWidget("guestbook")}>방명록 추가</Button>
                </Stack>

                <Divider />

                <Typography variant="subtitle2">꾸미기</Typography>
                <Box>
                    {layout.map((widget) => (
                        <Accordion key={widget.i} expanded={expanded === widget.i} onChange={handleAccordionChange(widget.i)}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography sx={{ flexGrow: 1, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {widget.type === 'about' && '소개 카드'}
                                    {widget.type === 'guestbook' && '방명록'}
                                    {widget.type === 'reactionBar' && '응원하기'}
                                    {widget.type === 'titleShowcase' && (getWidgetProp(widget.i, 'showcaseConfig', {})?.header ?? '칭호 진열장')}
                                    {widget.type === 'emoteShelf' && (getWidgetProp(widget.i, 'emoteConfig', {})?.header ?? '이모티콘')}
                                    {widget.type === 'textBox' && (getWidgetProp(widget.i, 'textConfig', {}).content?.split('\n')[0] || '텍스트 박스')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {widget.type === 'about' && (
                                    <Stack spacing={1.5}>
                                        <TextField label="소개 문구" multiline rows={3} value={getWidgetProp(widget.i, 'introText', '')} onChange={e => handleWidgetPropChange(widget.i, 'introText', e.target.value)} />
                                        <FormControlLabel control={<Checkbox checked={getWidgetProp(widget.i, 'isTransparent', false)} onChange={e => handleWidgetPropChange(widget.i, 'isTransparent', e.target.checked)} />} label="배경 투명화" />
                                        {!getWidgetProp(widget.i, 'isTransparent', false) && (
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <input type="color" value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')} onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)} style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer' }} />
                                                <TextField label="배경색" size="small" value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')} onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)} fullWidth />
                                            </Stack>
                                        )}
                                        <Divider sx={{ pt: 1 }} />
                                        <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => onRemoveWidget(widget.i)}>이 위젯 삭제</Button>
                                    </Stack>
                                )}
                                {widget.type === 'guestbook' && (
                                    <Stack spacing={1.5}>
                                        <FormControlLabel control={<Checkbox checked={getWidgetProp(widget.i, 'isTransparent', false)} onChange={e => handleWidgetPropChange(widget.i, 'isTransparent', e.target.checked)} />} label="배경 투명화" />
                                        {!getWidgetProp(widget.i, 'isTransparent', false) && (
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <input type="color" value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')} onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)} style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer' }} />
                                                <TextField label="배경색" size="small" value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')} onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)} fullWidth />
                                            </Stack>
                                        )}
                                        <Divider sx={{ pt: 1 }} />
                                        <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => onRemoveWidget(widget.i)}>이 위젯 삭제</Button>
                                    </Stack>
                                )}
                                {/* ✅ 아래 블록 전체 추가 (guestbook 꾸미기 UI와 내용이 동일합니다) */}
                                {widget.type === 'reactionBar' && (
                                    <Stack spacing={2}>
                                        <Typography variant="body2" color="text.secondary">표시할 응원 선택</Typography>
                                        <Stack direction="row" spacing={1}>
                                            {['heart', 'thumb', 'leaf'].map((type) => {
                                                const currentConfig = getWidgetProp(widget.i, 'reactionConfig', {});
                                                const isEnabled = (currentConfig.enabledTypes || []).includes(type);
                                                const label = type === 'heart' ? '하트' : type === 'thumb' ? '좋아요' : '리프';

                                                return (
                                                    <FormControlLabel
                                                        key={type}
                                                        control={
                                                            <Checkbox
                                                                checked={isEnabled}
                                                                onChange={(e) => {
                                                                    const oldTypes = currentConfig.enabledTypes || [];
                                                                    const newTypes = e.target.checked
                                                                        ? [...oldTypes, type]
                                                                        : oldTypes.filter(t => t !== type);
                                                                    handleWidgetPropChange(widget.i, 'reactionConfig', { ...currentConfig, enabledTypes: newTypes });
                                                                }}
                                                            />
                                                        }
                                                        label={label}
                                                    />
                                                );
                                            })}
                                        </Stack>

                                        <Divider />
                                        <Typography variant="body2" color="text.secondary">집계 방식</Typography>
                                        <ToggleButtonGroup
                                            value={getWidgetProp(widget.i, 'reactionConfig', {}).displayMode || 'daily'}
                                            exclusive
                                            size="small"
                                            fullWidth
                                            onChange={(_, newMode) => {
                                                if (newMode) {
                                                    const currentConfig = getWidgetProp(widget.i, 'reactionConfig', {});
                                                    handleWidgetPropChange(widget.i, 'reactionConfig', { ...currentConfig, displayMode: newMode });
                                                }
                                            }}
                                        >
                                            <Tooltip title="일일 집계">
                                                <ToggleButton value="daily">
                                                    <UpdateIcon />
                                                </ToggleButton>
                                            </Tooltip>
                                            <Tooltip title="누적 집계">
                                                <ToggleButton value="cumulative">
                                                    <FunctionsIcon />
                                                </ToggleButton>
                                            </Tooltip>
                                        </ToggleButtonGroup>

                                        <Divider />

                                        {/* 기존 배경 설정 UI */}
                                        <FormControlLabel control={<Checkbox checked={getWidgetProp(widget.i, 'isTransparent', false)} onChange={e => handleWidgetPropChange(widget.i, 'isTransparent', e.target.checked)} />} label="배경 투명화" />
                                        {!getWidgetProp(widget.i, 'isTransparent', false) && (
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <input type="color" value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')} onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)} style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer' }} />
                                                <TextField label="배경색" size="small" value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')} onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)} fullWidth />
                                            </Stack>
                                        )}
                                        <Divider sx={{ pt: 1 }} />
                                        <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => onRemoveWidget(widget.i)}>이 위젯 삭제</Button>
                                    </Stack>
                                )}
                                {widget.type === 'titleShowcase' && (
                                    <Stack spacing={2}>
                                        <TextField
                                            label="위젯 제목"
                                            size="small"
                                            value={getWidgetProp(widget.i, 'showcaseConfig', {}).header || ''}
                                            onChange={e => handleWidgetPropChange(widget.i, 'showcaseConfig', { ...getWidgetProp(widget.i, 'showcaseConfig', {}), header: e.target.value })}
                                        />
                                        <FormControl size="small">
                                            <InputLabel>표시할 칭호</InputLabel>
                                            <Select
                                                multiple
                                                value={getWidgetProp(widget.i, 'showcaseConfig', {}).selectedIds || []}
                                                onChange={(e) => handleWidgetPropChange(widget.i, 'showcaseConfig', { ...getWidgetProp(widget.i, 'showcaseConfig', {}), selectedIds: e.target.value })}
                                                input={<OutlinedInput label="표시할 칭호" />}
                                                renderValue={(selected) => `${selected.length}개 선택됨`}
                                            >
                                                {ownedTitles.map((title) => (
                                                    <MenuItem key={title.id} value={title.id}>
                                                        <Checkbox checked={(getWidgetProp(widget.i, 'showcaseConfig', {}).selectedIds || []).indexOf(title.id) > -1} />
                                                        <ListItemText primary={title.name} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <ToggleButtonGroup
                                            value={getWidgetProp(widget.i, 'showcaseConfig', {}).layoutMode || 'grid'}
                                            exclusive
                                            size="small"
                                            onChange={(_, v) => v && handleWidgetPropChange(widget.i, 'showcaseConfig', { ...getWidgetProp(widget.i, 'showcaseConfig', {}), layoutMode: v })}
                                        >
                                            <ToggleButton value="grid">격자</ToggleButton>
                                            <ToggleButton value="list">목록</ToggleButton>
                                        </ToggleButtonGroup>
                                        <Divider />
                                        <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>뱃지 색상</Typography>
                                        <Stack spacing={1.5}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <input
                                                    type="color"
                                                    value={getWidgetProp(widget.i, 'showcaseConfig', {}).badgeBackgroundColor || '#e0e0e0'}
                                                    onChange={e => handleWidgetPropChange(widget.i, 'showcaseConfig', { ...getWidgetProp(widget.i, 'showcaseConfig', {}), badgeBackgroundColor: e.target.value })}
                                                    style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer' }}
                                                />
                                                <TextField
                                                    label="뱃지 배경색"
                                                    size="small"
                                                    value={getWidgetProp(widget.i, 'showcaseConfig', {}).badgeBackgroundColor || '#e0e0e0'}
                                                    onChange={e => handleWidgetPropChange(widget.i, 'showcaseConfig', { ...getWidgetProp(widget.i, 'showcaseConfig', {}), badgeBackgroundColor: e.target.value })}
                                                    fullWidth
                                                />
                                            </Stack>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <input
                                                    type="color"
                                                    value={getWidgetProp(widget.i, 'showcaseConfig', {}).badgeTextColor || '#000000'}
                                                    onChange={e => handleWidgetPropChange(widget.i, 'showcaseConfig', { ...getWidgetProp(widget.i, 'showcaseConfig', {}), badgeTextColor: e.target.value })}
                                                    style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer' }}
                                                />
                                                <TextField
                                                    label="뱃지 글자색"
                                                    size="small"
                                                    value={getWidgetProp(widget.i, 'showcaseConfig', {}).badgeTextColor || '#000000'}
                                                    onChange={e => handleWidgetPropChange(widget.i, 'showcaseConfig', { ...getWidgetProp(widget.i, 'showcaseConfig', {}), badgeTextColor: e.target.value })}
                                                    fullWidth
                                                />
                                            </Stack>
                                        </Stack>
                                        <Divider sx={{ pt: 1 }} />
                                        <FormControlLabel
                                            control={<Checkbox checked={getWidgetProp(widget.i, 'isTransparent', false)} onChange={e => handleWidgetPropChange(widget.i, 'isTransparent', e.target.checked)} />}
                                            label="배경 투명화"
                                        />
                                        {!getWidgetProp(widget.i, 'isTransparent', false) && (
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <input type="color" value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')} onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)} style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer' }} />
                                                <TextField label="배경색" size="small" value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')} onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)} fullWidth />
                                            </Stack>
                                        )}
                                        <Divider sx={{ pt: 1 }} />
                                        <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => onRemoveWidget(widget.i)}>이 위젯 삭제</Button>
                                    </Stack>
                                )}
                                {widget.type === 'emoteShelf' && (
                                    <Stack spacing={1.5}>
                                        <TextField
                                            label="위젯 제목"
                                            size="small"
                                            value={getWidgetProp(widget.i, 'emoteConfig', {})?.header || ''}
                                            onChange={(e) =>
                                                handleWidgetPropChange(widget.i, 'emoteConfig', {
                                                    ...getWidgetProp(widget.i, 'emoteConfig', {}),
                                                    header: e.target.value,
                                                })
                                            }
                                        />
                                        <ToggleButtonGroup
                                            value={getWidgetProp(widget.i, 'emoteConfig', {})?.layoutMode || 'grid'}
                                            exclusive size="small"
                                            onChange={(_, v) => v && handleWidgetPropChange(widget.i, 'emoteConfig', {
                                                ...getWidgetProp(widget.i, 'emoteConfig', {}),
                                                layoutMode: v,
                                            })}
                                        >
                                            <ToggleButton value="grid">격자</ToggleButton>
                                            <ToggleButton value="list">목록</ToggleButton>
                                        </ToggleButtonGroup>
                                        <Stack direction="row" spacing={1}>
                                            <TextField
                                                label="크기(px)" type="number" size="small" sx={{ width: 130 }}
                                                value={getWidgetProp(widget.i, 'emoteConfig', {})?.size ?? 48}
                                                onChange={(e) => handleWidgetPropChange(widget.i, 'emoteConfig', {
                                                    ...getWidgetProp(widget.i, 'emoteConfig', {}),
                                                    size: Number(e.target.value || 48),
                                                })}
                                            />
                                            <TextField
                                                label="간격(px)" type="number" size="small" sx={{ width: 130 }}
                                                value={getWidgetProp(widget.i, 'emoteConfig', {})?.gap ?? 8}
                                                onChange={(e) => handleWidgetPropChange(widget.i, 'emoteConfig', {
                                                    ...getWidgetProp(widget.i, 'emoteConfig', {}),
                                                    gap: Number(e.target.value || 8),
                                                })}
                                            />
                                        </Stack>
                                        <FormControl size="small">
                                            <InputLabel>표시할 이모티콘</InputLabel>
                                            <Select
                                                multiple
                                                input={<OutlinedInput label="표시할 이모티콘" />}
                                                value={getWidgetProp(widget.i, 'emoteConfig', {})?.emotes || []}
                                                onChange={(e) => {
                                                    handleWidgetPropChange(widget.i, 'emoteConfig', {
                                                        ...getWidgetProp(widget.i, 'emoteConfig', {}),
                                                        emotes: e.target.value,
                                                    });
                                                }}
                                                renderValue={(selected) => selected.length ? `${selected.length}개 선택됨` : '선택 없음'}
                                            >
                                                {(ownedEmotes || []).map((em, idx) => {
                                                    const val = em?.value || em?.emoji || em?.url || "";
                                                    const label = em?.name || val || `이모티콘 ${idx + 1}`;
                                                    const isUrl = typeof val === "string" && (val.startsWith("/") || /^https?:\/\//i.test(val));
                                                    return (
                                                        <MenuItem key={`${label}-${idx}`} value={val}>
                                                            <Checkbox checked={(getWidgetProp(widget.i, 'emoteConfig', {}).emotes || []).indexOf(val) > -1} />
                                                            <Stack direction="row" spacing={1.2} alignItems="center">
                                                                {isUrl
                                                                    ? <Box component="img" src={val} alt="emote" sx={{ width: 28, height: 28, objectFit: "contain", border: "1px solid #eee", borderRadius: 0.5 }} />
                                                                    : <Box sx={{ fontSize: 22, lineHeight: 1 }}>{val}</Box>}
                                                                <ListItemText primary={label} />
                                                            </Stack>
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={getWidgetProp(widget.i, 'isTransparent', false)}
                                                    onChange={e => handleWidgetPropChange(widget.i, 'isTransparent', e.target.checked)}
                                                />
                                            }
                                            label="배경 투명화"
                                        />
                                        {!getWidgetProp(widget.i, 'isTransparent', false) && (
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <input
                                                    type="color"
                                                    value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')}
                                                    onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)}
                                                    style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer' }}
                                                />
                                                <TextField
                                                    label="배경색" size="small"
                                                    value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')}
                                                    onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)}
                                                    fullWidth
                                                />
                                            </Stack>
                                        )}
                                        <Divider sx={{ pt: 1 }} />
                                        <Button
                                            variant="outlined" color="error" size="small"
                                            startIcon={<DeleteIcon />} onClick={() => onRemoveWidget(widget.i)}
                                        >
                                            이 위젯 삭제
                                        </Button>
                                    </Stack>
                                )}
                                {widget.type === 'textBox' && (
                                    <Stack spacing={1.5}>
                                        <TextField
                                            label="내용 (Markdown 지원)"
                                            multiline
                                            rows={6}
                                            value={getWidgetProp(widget.i, 'textConfig', {}).content || ''}
                                            onChange={e => handleWidgetPropChange(widget.i, 'textConfig', {
                                                ...getWidgetProp(widget.i, 'textConfig', {}),
                                                content: e.target.value
                                            })}
                                        />
                                        {/* 👇 --- 여기에 정렬 버튼 그룹 코드 추가 --- 👇 */}
                                        <ToggleButtonGroup
                                            value={getWidgetProp(widget.i, 'textConfig', {}).textAlign || 'left'}
                                            exclusive
                                            size="small"
                                            onChange={(_, newAlign) => {
                                                if (newAlign) {
                                                    handleWidgetPropChange(widget.i, 'textConfig', {
                                                        ...getWidgetProp(widget.i, 'textConfig', {}),
                                                        textAlign: newAlign,
                                                    });
                                                }
                                            }}
                                            aria-label="text alignment"
                                        >
                                            <ToggleButton value="left" aria-label="left aligned">
                                                <FormatAlignLeftIcon />
                                            </ToggleButton>
                                            <ToggleButton value="center" aria-label="centered">
                                                <FormatAlignCenterIcon />
                                            </ToggleButton>
                                            <ToggleButton value="right" aria-label="right aligned">
                                                <FormatAlignRightIcon />
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                        {/* 👆 --- 여기까지 추가 --- 👆 */}
                                        <FormControlLabel
                                            control={<Checkbox checked={getWidgetProp(widget.i, 'isTransparent', false)} onChange={e => handleWidgetPropChange(widget.i, 'isTransparent', e.target.checked)} />}
                                            label="배경 투명화"
                                        />




                                        {!getWidgetProp(widget.i, 'isTransparent', false) && (
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <input type="color" value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')} onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)} style={{ width: 40, height: 40, border: 'none', background: 'none', cursor: 'pointer' }} />
                                                <TextField label="배경색" size="small" value={getWidgetProp(widget.i, 'backgroundColor', '#ffffff')} onChange={e => handleWidgetPropChange(widget.i, 'backgroundColor', e.target.value)} fullWidth />
                                            </Stack>
                                        )}
                                        <Divider sx={{ pt: 1 }} />
                                        <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => onRemoveWidget(widget.i)}>이 위젯 삭제</Button>
                                    </Stack>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>

                <Box flexGrow={1} />
                {saveOk && <Alert severity="success">{saveOk}</Alert>}
                {saveErr && <Alert severity="error">{saveErr}</Alert>}
                <Button variant="contained" onClick={onSave} disabled={saving} sx={{ mt: 2 }}>{saving ? "저장 중…" : "저장"}</Button>
            </Stack>

            <Box sx={{ p: 2, position: 'relative', ...backgroundStyle }}>
                <GridLayout
                    className="layout"
                    layout={rglLayout}
                    cols={DEFAULT_GRID.cols}
                    rowHeight={DEFAULT_GRID.rowHeight}
                    margin={DEFAULT_GRID.margin}
                    isBounded={false}
                    preventCollision={true}
                    compactType={null}  // ⬅️ 이 옵션을 추가하여 자동 정렬을 끕니다.
                    onLayoutChange={onLayoutChange}
                    cancel=".no-drag"
                >
                    {layout.map((item) => {
                        const Comp = WidgetRegistry[item.type];
                        if (!Comp) {
                            return (<div key={item.i} style={{ background: 'red', color: 'white' }}>오류: '{item.type}' 위젯 없음</div>);
                        }
                        return (
                            <div key={item.i} style={{ border: item.isTransparent ? '1px dashed #999' : '1px solid #ddd', zIndex: 1, background: item.backgroundColor }}>
                                <Comp home={homeForWidget} item={item} canModerate username={username} />
                            </div>
                        );
                    })}
                </GridLayout>
            </Box>
        </Box>
    );
}